import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import FormField from '../FormField';
import AgencyAutocomplete from '../AgencyAutocomplete';

const OTHER_VALUE = '__OTHER__';

// Free-text input with an × clear button and blur-to-cancel behaviour.
// Blurring with an empty value resets the field back to autocomplete mode.
const CustomEntryInput = ({ label, placeholder, value, onChange, onClear, disabled }) => {
  const inputRef = useRef(null);

  // Focus the input after mount (deferred so Headless UI finishes its blur handling first)
  useEffect(() => {
    const id = setTimeout(() => { inputRef.current?.focus(); }, 50);
    return () => clearTimeout(id);
  }, []);

  return (
    <div className="flex flex-col gap-1">
      <label
        className="text-[12px] font-normal leading-[16px] text-[#252525] mb-2"
        style={{ fontFamily: 'Poppins, sans-serif' }}
      >
        {label}
      </label>
      <div className="relative">
        <input
          type="text"
          placeholder={placeholder}
          value={value}
          ref={inputRef}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => { if (!value.trim()) onClear(); }}
          disabled={disabled}
          className="
            w-full h-[56px] pl-4 pr-10 text-[14px] font-normal leading-[18px] text-[#252525]
            bg-white border border-gray-300 rounded-lg
            focus:outline-none focus:ring-2 focus:ring-[#49C44F] focus:border-transparent
            placeholder:text-[#6E6E6E]
            disabled:opacity-50 disabled:cursor-not-allowed
          "
          style={{ fontFamily: 'Poppins, sans-serif' }}
        />
        {value && !disabled && (
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); onClear(); }}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
            aria-label="Clear"
          >
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 8.586L4.707 3.293a1 1 0 00-1.414 1.414L8.586 10l-5.293 5.293a1 1 0 001.414 1.414L10 11.414l5.293 5.293a1 1 0 001.414-1.414L11.414 10l5.293-5.293a1 1 0 00-1.414-1.414L10 8.586z" clipRule="evenodd" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

const AdvisorInfoFields = ({
  formData,
  handleInputChange,
  loading,
  consortiumOptions,
  agencyOptions,
  subAgencyOptions,
  isLoadingConsortiums,
  isLoadingAgencies,
  isLoadingSubAgencies,
  hideAgencyManualEntry = false,
  lockAgencyFields = false
}) => {
  // Local flags control which component is rendered — independent of Headless UI's onChange calls.
  // When onNotFound fires we flip the flag immediately; Headless UI may still call onChange after
  // that but it won't affect the render because we're keying off these flags, not formData.
  const [customConsortium, setCustomConsortium] = useState(false);
  const [customAgency, setCustomAgency] = useState(false);
  const [customSubAgency, setCustomSubAgency] = useState(false);

  // Keep local flags in sync when formData is reset externally (e.g. form clear)
  useEffect(() => {
    if (formData.consortium !== OTHER_VALUE) setCustomConsortium(false);
  }, [formData.consortium]);
  useEffect(() => {
    if (formData.agency !== OTHER_VALUE) setCustomAgency(false);
  }, [formData.agency]);
  useEffect(() => {
    if (formData.subAgency !== OTHER_VALUE) setCustomSubAgency(false);
  }, [formData.subAgency]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
      <div>
        {customConsortium ? (
          <CustomEntryInput
            label="Consortium name"
            placeholder="Enter consortium name"
            value={formData.otherConsortiumName}
            onChange={(value) => handleInputChange('otherConsortiumName', value)}
            onClear={() => { setCustomConsortium(false); handleInputChange('consortium', ''); }}
            disabled={loading || lockAgencyFields}
          />
        ) : (
          <FormField
            label="Consortium"
            placeholder="Search for consortium..."
            value={formData.consortium}
            onChange={(value) => handleInputChange('consortium', value)}
            type="autocomplete"
            options={consortiumOptions}
            disabled={loading || isLoadingConsortiums || lockAgencyFields}
            onNotFound={(query) => {
              setCustomConsortium(true);
              handleInputChange('consortium', OTHER_VALUE);
              if (query) handleInputChange('otherConsortiumName', query);
            }}
            notFoundLabel="consortium"
          />
        )}
      </div>
      <div>
        {customAgency ? (
          <CustomEntryInput
            label="Primary/Host Agency name"
            placeholder="Enter primary/host agency name"
            value={formData.otherAgencyName}
            onChange={(value) => handleInputChange('otherAgencyName', value)}
            onClear={() => { setCustomAgency(false); handleInputChange('agency', ''); }}
            disabled={loading || lockAgencyFields}
          />
        ) : (
          <AgencyAutocomplete
            label="Primary/Host Agency"
            placeholder="Search for primary/host agency..."
            value={formData.agency}
            onChange={(value) => handleInputChange('agency', value)}
            consortiumId={formData.consortium !== OTHER_VALUE ? formData.consortium : undefined}
            disabled={loading || !formData.consortium || lockAgencyFields}
            onNotFound={hideAgencyManualEntry ? null : (query) => {
              setCustomAgency(true);
              handleInputChange('agency', OTHER_VALUE);
              if (query) handleInputChange('otherAgencyName', query);
            }}
            notFoundLabel="primary/host agency"
          />
        )}
      </div>
      <div>
        {customSubAgency ? (
          <CustomEntryInput
            label="Branch/Agency name (optional)"
            placeholder="Enter branch/agency name"
            value={formData.otherSubAgencyName}
            onChange={(value) => handleInputChange('otherSubAgencyName', value)}
            onClear={() => { setCustomSubAgency(false); handleInputChange('subAgency', ''); }}
            disabled={loading || lockAgencyFields}
          />
        ) : (
          <AgencyAutocomplete
            label="Branch/Agency"
            placeholder="Search for branch/agency..."
            value={formData.subAgency}
            onChange={(value) => handleInputChange('subAgency', value)}
            parentAgencyId={formData.agency !== OTHER_VALUE ? formData.agency : undefined}
            disabled={loading || !formData.agency || lockAgencyFields}
            onNotFound={hideAgencyManualEntry ? null : (query) => {
              setCustomSubAgency(true);
              handleInputChange('subAgency', OTHER_VALUE);
              if (query) handleInputChange('otherSubAgencyName', query);
            }}
            notFoundLabel="branch/agency"
          />
        )}
      </div>
    </div>
  );
};

CustomEntryInput.propTypes = {
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired
};

AdvisorInfoFields.propTypes = {
  formData: PropTypes.object.isRequired,
  handleInputChange: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  consortiumOptions: PropTypes.array.isRequired,
  agencyOptions: PropTypes.array.isRequired,
  subAgencyOptions: PropTypes.array.isRequired,
  isLoadingConsortiums: PropTypes.bool.isRequired,
  isLoadingAgencies: PropTypes.bool.isRequired,
  isLoadingSubAgencies: PropTypes.bool.isRequired,
  hideAgencyManualEntry: PropTypes.bool,
  lockAgencyFields: PropTypes.bool
};

export default AdvisorInfoFields;
